import React from 'react';

import cs from 'classnames';
import Tippy from '@tippyjs/react';
import orderBy from 'lodash/orderBy';

import { ia, pluralize } from 'lib/helpers/utility';
import { useTags } from 'lib/hooks/queries/tags/useTags';
import { useUIContext } from 'lib/context/UIContext/UIContext';
import { useTagContext } from 'lib/context/TagContext/TagContext';

import ErrorBoundary from 'components/shared/Error/Boundary';

import TagPill from '../TagPill/TagPill';

import WrappedView from './components/WrappedView';
import OrderedView from './components/OrderedView';

/**
 * @param {import('./lib/propTypes').TagsViewProps} props
 */

const TagsView = (props) => {
  const {
    currTags,
    kind,
    itemsToShow = 2,
    label = 'Tags',
    className,
    appendTo = 'parent',
    handleRemove = () => {},
    fromTable = false,
    fromPatientChart = false,
    fromFaxes = false,
    disableDeviceCheck = false,
    fromAppointment = false,
    readOnly = false,
    layout = 'inline'
  } = props;

  const { formattedTags } = useTagContext() || {};
  const { data: loadedTags } = useTags({
    params: { kind },
    dependencies: [kind],
    options: { enabled: !!kind }
  });
  const shouldUseFormattedTags = !fromFaxes && (fromTable || ia(formattedTags));
  const currentTags = currTags
    ? orderBy(currTags, (tag) => tag?.important, 'desc')
    : loadedTags?.tags;
  const tags = shouldUseFormattedTags ? formattedTags : currentTags;
  const { device } = useUIContext();
  const showAllTags = disableDeviceCheck ? itemsToShow : device === 'desktop' ? itemsToShow : 0;

  if (layout === 'wrap') {
    return (
      <ErrorBoundary>
        <WrappedView
          tags={currTags ? orderBy(currTags, (tag) => !!tag?.important, 'desc') : tags}
          label={label}
          className={className}
        />
      </ErrorBoundary>
    );
  }

  const renderTag = (tag) => (
    <TagPill tag={tag} onRemove={!fromAppointment && !readOnly ? handleRemove : undefined} />
  );

  return (
    <ErrorBoundary>
      <div className={cs('overflow-hidden', className)}>
        {label && <label className="mb-1 block text-sm text-neutral-900">{label}</label>}
        <div className="flex min-w-0 items-center gap-2">
          {device === 'desktop' || (device !== 'mobile' && !fromPatientChart) ? (
            <div
              className="grid min-w-0 items-center gap-2"
              style={{ gridTemplateColumns: `repeat(${itemsToShow},minmax(0,auto))` }}>
              {tags?.slice(0, itemsToShow).map((tag, index) =>
                fromTable || fromPatientChart ? (
                  <Tippy
                    appendTo={appendTo}
                    content={tag?.name}
                    className="tippy-dark text-base"
                    key={tag?.id ?? index}>
                    <div className="min-w-0">{renderTag(tag)}</div>
                  </Tippy>
                ) : (
                  <React.Fragment key={tag?.id ?? index}>{renderTag(tag)}</React.Fragment>
                )
              )}
            </div>
          ) : device === 'mobile' && !fromPatientChart && !fromTable ? (
            <Tippy
              appendTo={appendTo}
              className="custom-tooltip !max-w-[150px] !rounded-[5px] !p-3 !pr-2 shadow-[0px_0px_16px_rgba(0,79,107,0.24)]"
              content={
                <div className="flex flex-col gap-[10px] overflow-y-auto overflow-x-hidden truncate !pr-2">
                  {tags?.map((tag, index) => (
                    <Tippy
                      content={tag?.name}
                      className="tippy-dark text-base"
                      key={tag?.id ?? index}>
                      <div>{renderTag(tag)}</div>
                    </Tippy>
                  ))}
                </div>
              }
              placement={fromTable ? 'bottom' : 'right'}
              interactive>
              <span className="select-none rounded border border-solid border-primary-200 bg-primary-100 px-2 py-[1px] text-xs font-500 leading-5 text-neutral-900">
                {`+${tags?.length}`}
              </span>
            </Tippy>
          ) : null}
          {tags?.length > showAllTags ? (
            fromPatientChart ? (
              <Tippy
                appendTo={appendTo}
                className="custom-tooltip !max-w-[360px] !rounded-[5px] !p-3 !pr-2 shadow-[0px_0px_16px_rgba(0,79,107,0.24)]"
                content={
                  <OrderedView
                    handleRemove={readOnly ? undefined : handleRemove}
                    showAllTags={showAllTags}
                  />
                }
                interactive>
                <span className="flex shrink-0 select-none items-center rounded border border-solid border-primary-200 bg-primary-100 px-2 py-[1px] text-xs font-500 leading-5 text-neutral-900">
                  {device !== 'desktop'
                    ? `${tags?.length} ${pluralize('tag', tags?.length)}`
                    : tags?.length - itemsToShow > 0 && `+${tags?.length - itemsToShow}`}
                </span>
              </Tippy>
            ) : (
              <Tippy
                appendTo={appendTo}
                className="custom-tooltip !max-w-[150px] !rounded-[5px] !p-3 !pr-2 shadow-[0px_0px_16px_rgba(0,79,107,0.24)]"
                content={
                  <div className="flex flex-col gap-[10px] overflow-y-auto overflow-x-hidden truncate !pr-2">
                    {tags?.slice(showAllTags).map((tag, index) =>
                      tag?.name?.length > 10 ? (
                        <Tippy
                          content={tag?.name}
                          className="tippy-dark text-base"
                          key={tag?.id ?? index}>
                          <div>{renderTag(tag)}</div>
                        </Tippy>
                      ) : (
                        <React.Fragment key={tag?.id ?? index}>{renderTag(tag)}</React.Fragment>
                      )
                    )}
                  </div>
                }
                placement={fromTable ? 'bottom' : 'right'}
                interactive>
                <span className="flex shrink-0 select-none items-center rounded border border-solid border-primary-200 bg-primary-100 px-2 py-[1px] text-xs font-500 leading-5 text-neutral-900">
                  {`+${tags?.length - showAllTags}`}
                </span>
              </Tippy>
            )
          ) : null}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default TagsView;
